/**
 * The event, said back in one sentence.
 *
 * The form is fourteen controls and a file preview; this is the line someone actually re-reads
 * before downloading. It exists because the fields are each correct in isolation and the whole
 * is what people get wrong — an event on the wrong day, an alert nobody meant to set, a repeat
 * left on from an earlier edit.
 *
 * Formatting only. Everything here is derived from the draft, so it cannot disagree with the
 * file: if the sentence says Sunday, `DTSTART` says Sunday.
 *
 * Two locales are at work in this one line, and they are deliberately not the same one. The
 * words come from the catalogue the host chose; the date and the clock come from the reader's
 * own browser, because `Sun 16 Aug` and `16.08.` are a question about where someone reads, not
 * about what language a page is written in — and the `type="date"` control directly above this
 * sentence has always answered it that way.
 */

import { type EventDraft, timeClock, timeDate } from '@project/event-format';
import { eventDurationMinutes } from '@project/event-creator';

import { EN_MESSAGES, fill, plural, type EventCreatorMessages } from './messages';

function zonedInstant(dateTime: string, timeZone: string): number | null {
  const wall = Date.parse(`${dateTime}Z`);
  if (!Number.isFinite(wall)) return null;
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const clockAt = (instant: number): number => {
      const parts = Object.fromEntries(
        formatter.formatToParts(instant).map((part) => [part.type, part.value]),
      );
      return Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second),
      );
    };
    const offsets = new Set(
      [-36, 0, 36].map((hours) => {
        const sample = wall + hours * 3600000;
        return clockAt(sample) - sample;
      }),
    );
    const candidates = [...offsets]
      .map((offset) => wall - offset)
      .filter((instant) => clockAt(instant) === wall);
    return candidates.length === 1 ? candidates[0]! : null;
  } catch {
    return null;
  }
}

export function previewDurationMinutes(draft: EventDraft): number | null {
  if (draft.start.kind === 'date' || draft.end?.kind !== 'dateTime')
    return eventDurationMinutes(draft);
  const startZone = draft.start.timeZone;
  const endZone = draft.end.timeZone;
  if (!startZone && !endZone) return eventDurationMinutes(draft);
  if (!startZone || !endZone) return null;
  const start = zonedInstant(draft.start.dateTime, startZone);
  const end = zonedInstant(draft.end.dateTime, endZone);
  return start === null || end === null ? null : (end - start) / 60000;
}

/** The alert ladder, in the recap's lower-case voice. Same rungs as the form's own menu. */
function alertWords(minutes: number, m: EventCreatorMessages, locale: string | undefined): string {
  if (minutes === 0) return m.recapAlertAtTime;
  if (minutes < 60) return plural(m.alertMinutesBefore, minutes, locale);
  if (minutes < 1440) return plural(m.alertHoursBefore, minutes / 60, locale);
  if (minutes < 10080) return plural(m.alertDaysBefore, minutes / 1440, locale);
  return plural(m.alertWeeksBefore, minutes / 10080, locale);
}

/**
 * `Sun 16 Aug 2026`, in the reader's own language. Parsed as UTC and formatted as UTC, because a
 * draft's date is a calendar date rather than an instant — treating `2026-08-16` as local
 * midnight puts it on the 15th for anyone west of Greenwich.
 */
function dayWords(date: string): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const at = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(at.getTime())) return date;
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(at);
  } catch {
    return date;
  }
}

/**
 * `09:00` or `09:00 AM`, whichever the reader's own locale uses — the same choice the `type="time"`
 * picker above the sentence makes. The hour is padded in both, which is what the picker shows and
 * what keeps a pair of times the same width either side of the dash.
 *
 * Formatted as UTC on an arbitrary day, because a draft's clock is a wall-clock reading rather
 * than an instant: the zone it belongs to is said separately.
 */
function clockWords(clock: string | null): string {
  if (clock === null) return '';
  const [hour = 0, minute = 0] = clock.split(':').map(Number);
  const at = new Date(Date.UTC(1970, 0, 1, hour, minute));
  if (Number.isNaN(at.getTime())) return clock.slice(0, 5);
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    }).format(at);
  } catch {
    return clock.slice(0, 5);
  }
}

function everyWords(
  draft: NonNullable<EventDraft['recurrence']>,
  m: EventCreatorMessages,
  locale: string | undefined,
): string {
  const interval = draft.interval ?? 1;
  const unit = {
    daily: m.unitDays,
    weekly: m.unitWeeks,
    monthly: m.unitMonths,
    yearly: m.unitYears,
  }[draft.frequency];
  const unitWords = plural(unit, interval, locale);
  return interval === 1
    ? fill(m.recapEveryOne, { unit: unitWords })
    : fill(m.recapEveryMany, { count: interval, unit: unitWords });
}

function repeatWords(
  draft: EventDraft,
  m: EventCreatorMessages,
  locale: string | undefined,
): string | null {
  const recurrence = draft.recurrence;
  if (recurrence === undefined) return null;
  const every = everyWords(recurrence, m, locale);
  if (recurrence.count !== undefined)
    return plural(m.recapRepeatsCount, recurrence.count, locale, { every });
  if (recurrence.until !== undefined && recurrence.until !== '')
    return fill(m.recapRepeatsUntil, { every, date: dayWords(recurrence.until) });
  return fill(m.recapRepeats, { every });
}

function whenWords(draft: EventDraft, m: EventCreatorMessages): string {
  const start = draft.start;
  const end = draft.end;
  const startDay = dayWords(timeDate(start));
  if (start.kind === 'date') {
    const endDay = end === undefined ? startDay : dayWords(timeDate(end));
    return endDay === startDay
      ? fill(m.recapAllDay, { day: startDay })
      : fill(m.recapAllDayRange, { start: startDay, end: endDay });
  }
  const startClock = clockWords(timeClock(start));
  const endClock = end === undefined ? '' : clockWords(timeClock(end));
  const endDay = end === undefined ? startDay : dayWords(timeDate(end));
  // Same day is the common case, and repeating the date either side of a dash reads as noise.
  if (endClock === '') return fill(m.recapTimed, { day: startDay, clock: startClock });
  return endDay === startDay
    ? fill(m.recapTimedRange, { day: startDay, start: startClock, end: endClock })
    : fill(m.recapTimedRangeDays, {
        startDay,
        startClock,
        endDay,
        endClock,
      });
}

function zoneWords(draft: EventDraft, m: EventCreatorMessages): string | null {
  const startZone = draft.start.kind === 'dateTime' ? draft.start.timeZone : undefined;
  const endZone = draft.end?.kind === 'dateTime' ? draft.end.timeZone : undefined;
  if (startZone === undefined && endZone === undefined) return null;
  const short = (zone: string | undefined): string =>
    zone === undefined ? m.recapNoZone : (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
  if (endZone === undefined || endZone === startZone) return short(startZone);
  return `${short(startZone)} → ${short(endZone)}`;
}

/**
 * One line: when it is, where it is, what it repeats, who is coming, what will warn you.
 * Untitled events get "This event" rather than a gap, since the sentence is most useful exactly
 * when the form is still incomplete.
 */
export function eventRecap(
  draft: EventDraft,
  m: EventCreatorMessages = EN_MESSAGES,
  locale?: string,
  includeTitle = true,
): string {
  const parts = [whenWords(draft, m)];
  const zone = zoneWords(draft, m);
  if (zone !== null) parts.push(zone);
  if (draft.location !== undefined && draft.location !== '') parts.push(draft.location);
  const repeat = repeatWords(draft, m, locale);
  if (repeat !== null) parts.push(repeat);

  const invitees = draft.attendees ?? [];
  if (invitees.length > 0) parts.push(plural(m.recapInvitees, invitees.length, locale));

  const alerts = draft.remindersMinutesBefore ?? [];
  if (alerts.length === 1)
    parts.push(fill(m.recapAlertOne, { words: alertWords(alerts[0] as number, m, locale) }));
  else if (alerts.length > 1) parts.push(plural(m.recapAlerts, alerts.length, locale));

  if (draft.showAs === 'free') parts.push(m.tallyShownAsFree);

  const title = draft.title.trim();
  return includeTitle
    ? `${title === '' ? m.recapUntitled : title} — ${parts.join(' · ')}`
    : parts.join(' · ');
}
